import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { GLOSSARY, GlossaryEntry } from '../../../../core/glossary/glossary';

export interface GlossaryPanelData {
  /** The keys of the terms on the page it was opened from, in page order. */
  readonly pageKeys: readonly string[];
}

/**
 * *What these terms mean* (FLOW-348, DOC-80 UI-DA-80-05): the whole glossary,
 * searchable by typing, the current page's terms first. The panel IS the
 * glossary — the same list the pop-ups read; *In this case* is not here, it
 * belongs to the term where it stands on an application's page.
 */
@Component({
  selector: 'app-glossary-panel',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './glossary-panel.html',
  styleUrl: './glossary-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlossaryPanelComponent {
  private readonly data = inject<GlossaryPanelData>(MAT_DIALOG_DATA);

  protected readonly search = new FormControl('', { nonNullable: true });
  private readonly query = toSignal(this.search.valueChanges, { initialValue: '' });

  /** Page terms first (in page order), then the rest in the glossary's order. */
  private readonly ordered: readonly GlossaryEntry[] = (() => {
    const onPage = this.data.pageKeys
      .map((key) => GLOSSARY.find((e) => e.key === key))
      .filter((e): e is GlossaryEntry => !!e);
    return [...onPage, ...GLOSSARY.filter((e) => !onPage.includes(e))];
  })();

  protected readonly onPage = new Set(this.data.pageKeys);

  protected readonly entries = computed(() => {
    const q = this.query().trim().toLowerCase();
    if (!q) return this.ordered;
    return this.ordered.filter(
      (e) => e.term.toLowerCase().includes(q) || e.meaning.toLowerCase().includes(q),
    );
  });
}
